import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTransactionsAggregate } from '../utils/useTransactionsAggregate';
import { useAppointmentsMonthlyAggregate } from '../utils/useAppointmentsMonthlyAggregate';
import { useTransactionTypes } from '../contexts/TransactionTypesContext';
import RevenueChart from '../components/Dashboard/RevenueChart';
import MonthlyFinancialTable from '../components/Dashboard/MonthlyFinancialTable';
import AppointmentsMonthSummary from '../components/Dashboard/AppointmentsMonthSummary';
import { Calendar, BarChart3, Download } from 'lucide-react';
import useConfirmation from '../hooks/useConfirmation';
import ConfirmationModal from '../components/ConfirmationModal';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    totalTransactions: 0
  });
  const [financialSummary, setFinancialSummary] = useState({
    lucroLiquido: 0
  });
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  // Por padrão: ano atual, sem filtro de mês (mostrar todos os meses)
  const [yearFilter, setYearFilter] = useState(() => new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [monthlyData, setMonthlyData] = useState({ summary: [], revenueTypes: [], expenseTypes: [] });
  
  // Usar apenas agregações de transações (atualização em tempo real)
  const { transactions: allTransactions, availableYears: yearsFromTransactions, loading: transactionsLoading } = useTransactionsAggregate();
  const {
    monthlyAggregates: appointmentsMonthlyAggregates,
    availableYears: appointmentYears,
    loading: appointmentsLoading,
  } = useAppointmentsMonthlyAggregate();
  const { transactionTypes, loading: typesLoading } = useTransactionTypes();
  const { confirmation, showConfirmation, hideConfirmation, confirmAction, cancelAction } = useConfirmation();

  // Usar anos disponíveis do agregado de transações
  useEffect(() => {
    if (yearsFromTransactions && yearsFromTransactions.length > 0) {
      setAvailableYears(yearsFromTransactions);
      
      // Se o ano atual não estiver na lista, definir o primeiro ano disponível como padrão
      if (!yearsFromTransactions.includes(parseInt(yearFilter))) {
        setYearFilter(yearsFromTransactions[0].toString());
      }
    } else if (!transactionsLoading) {
      // Se não houver anos ainda e não estiver carregando, usar o ano atual como fallback
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear]);
    }
  }, [yearsFromTransactions, transactionsLoading, yearFilter]);

  // Função para obter data da transação
  const getTransactionDate = (transaction) => {
    const dateField = transaction.data || transaction.date;
    if (!dateField) return null;
    try {
      if (dateField instanceof Date) {
        return dateField;
      }
      if (dateField?.toDate) {
        return dateField.toDate();
      }
      return new Date(dateField);
    } catch (error) {
      return null;
    }
  };

  // Função para filtrar transações pelo período
  const getFilteredTransactions = () => {
    return (allTransactions || []).filter(transaction => {
      const date = getTransactionDate(transaction);
      if (!date || isNaN(date.getTime())) return false;

      // Filtro por ano
      if (yearFilter) {
        if (date.getFullYear().toString() !== yearFilter) return false;
      }

      // Filtro por mês
      if (monthFilter) {
        if ((date.getMonth() + 1).toString() !== monthFilter) return false;
      }

      return true;
    }).sort((a, b) => {
      const dateA = getTransactionDate(a);
      const dateB = getTransactionDate(b);
      if (!dateA || isNaN(dateA.getTime())) return 1;
      if (!dateB || isNaN(dateB.getTime())) return -1;
      return dateB.getTime() - dateA.getTime(); // Mais recente primeiro
    });
  };

  // Processar dados a partir das transações agregadas
  useEffect(() => {
    if (transactionsLoading || typesLoading) {
      setLoading(true);
      return;
    }

    processDataFromTransactions();
  }, [transactionsLoading, typesLoading, yearFilter, monthFilter, allTransactions, transactionTypes]);

  // Função para processar dados a partir das transações agregadas
  const processDataFromTransactions = () => {
    const filteredTransactions = getFilteredTransactions();
    const targetYear = yearFilter ? parseInt(yearFilter) : new Date().getFullYear();
    
    // Calcular totais
    let totalRevenue = filteredTransactions
      .filter(t => t.tipo === 'receita')
      .reduce((sum, t) => sum + parseFloat(t.valor || t.amount || 0), 0);
    
    let totalExpenses = filteredTransactions
      .filter(t => t.tipo === 'despesa')
      .reduce((sum, t) => sum + parseFloat(t.valor || t.amount || 0), 0);

    const lucroLiquido = totalRevenue - totalExpenses;
    
    // Processar dados mensais
    const monthlyDataMap = new Map(); // monthIndex -> { receita, despesa }
    const dailyDataMap = new Map(); // day -> { receita, despesa }
    const revenueTypeMonthly = new Map(); // typeName -> Array(12) de valores
    const expenseTypeMonthly = new Map(); // typeName -> Array(12) de valores
    
    filteredTransactions.forEach(transaction => {
      const date = getTransactionDate(transaction);
      if (!date || isNaN(date.getTime())) return;
      
      const monthIndex = date.getMonth();
      const day = date.getDate();
      const valor = parseFloat(transaction.valor || transaction.amount || 0);
      
      // Atualizar dados mensais
      if (!monthlyDataMap.has(monthIndex)) {
        monthlyDataMap.set(monthIndex, { receita: 0, despesa: 0 });
      }
      const monthData = monthlyDataMap.get(monthIndex);
      
      if (transaction.tipo === 'receita') {
        monthData.receita += valor;
      } else if (transaction.tipo === 'despesa') {
        monthData.despesa += valor;
      }
      
      // Atualizar dados diários (apenas se há filtro de mês)
      if (monthFilter && date.getFullYear() === targetYear && date.getMonth() === parseInt(monthFilter) - 1) {
        const dayKey = day.toString();
        if (!dailyDataMap.has(dayKey)) {
          dailyDataMap.set(dayKey, { receita: 0, despesas: 0 });
        }
        const dayData = dailyDataMap.get(dayKey);
        
        if (transaction.tipo === 'receita') {
          dayData.receita += valor;
        } else if (transaction.tipo === 'despesa') {
          dayData.despesas += valor;
        }
      }
      
      // Processar por tipo de transação
      if (transaction.nomeTipoTransacao) {
        // Normalizar o nome do tipo (case-insensitive) para agrupar corretamente
        const typeName = transaction.nomeTipoTransacao.trim().toLowerCase();
        
        if (transaction.tipo === 'receita') {
          if (!revenueTypeMonthly.has(typeName)) {
            revenueTypeMonthly.set(typeName, Array(12).fill(0));
          }
          const typeMonthly = revenueTypeMonthly.get(typeName);
          typeMonthly[monthIndex] += valor;
        } else if (transaction.tipo === 'despesa') {
          if (!expenseTypeMonthly.has(typeName)) {
            expenseTypeMonthly.set(typeName, Array(12).fill(0));
          }
          const typeMonthly = expenseTypeMonthly.get(typeName);
          typeMonthly[monthIndex] += valor;
        }
      }
    });
    
    // Criar array de agregação mensal
    const monthlyAggregation = Array(12).fill(null);
    monthlyDataMap.forEach((data, monthIndex) => {
      monthlyAggregation[monthIndex] = data;
    });
    
    // Criar dados do gráfico
    let chartDataArray = [];
    
    if (monthFilter) {
      // Dados diários do mês
      const targetMonth = parseInt(monthFilter) - 1;
      const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(targetYear, targetMonth, day);
        const label = format(date, 'dd', { locale: ptBR }); // Apenas o dia
        const dayData = dailyDataMap.get(day.toString()) || { receita: 0, despesas: 0 };
        
        chartDataArray.push({
          nome: label,
          receita: dayData.receita || 0,
          despesas: dayData.despesas || 0
        });
      }
    } else {
      // Dados mensais do ano
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      
      monthlyAggregation.forEach((monthData, index) => {
        chartDataArray.push({
          nome: months[index],
          receita: monthData ? monthData.receita : 0,
          despesas: monthData ? (monthData.despesa || monthData.despesas || 0) : 0
        });
      });
    }
    
    // Criar arrays de tipos de receita e despesa
    const revenueTypesArray = Array.from(revenueTypeMonthly.keys())
      .map(typeName => ({
        typeId: typeName,
        typenome: typeName || '—',
        monthData: revenueTypeMonthly.get(typeName).map(receita => ({ receita }))
      }))
      .sort((a, b) => a.typenome.localeCompare(b.typenome));
    
    const expenseTypesArray = Array.from(expenseTypeMonthly.keys())
      .map(typeName => ({
        typeId: typeName,
        typenome: typeName || '—',
        monthData: expenseTypeMonthly.get(typeName).map(despesa => ({ despesa, despesaSemMaquina: 0 }))
      }))
      .sort((a, b) => a.typenome.localeCompare(b.typenome));
    
    setStats({
      totalRevenue,
      totalExpenses,
      totalTransactions: filteredTransactions.length
    });
    
    setFinancialSummary({
      lucroLiquido
    });
    
    setChartData(chartDataArray);
    
    setMonthlyData({
      summary: monthlyAggregation,
      revenueTypes: revenueTypesArray,
      expenseTypes: expenseTypesArray
    });
    
    setLoading(false);
  };

  // Função auxiliar para sanitizar texto para WinAnsi (remover caracteres não suportados)
  const sanitizeTextForPDF = (text) => {
    if (!text) return '';
    return text.toString()
      .replace(/[^\x20-\x7E\u00A0-\u00FF]/g, '') // Manter apenas ASCII e Latin-1
      .replace(/—/g, '-') // Substituir em dash por hífen
      .replace(/–/g, '-') // Substituir en dash por hífen
      .trim();
  };

  // Função auxiliar para desenhar a tabela de Resumo Financeiro no PDF
  // startX = posição X inicial, availableWidth = largura disponível para a tabela
  const drawMonthlyFinancialTableInPDF = (page, monthlyData, startX, startY, availableWidth, font, boldFont) => {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    
    const tableMargin = 0; // Margem zerada conforme solicitado
    const rowHeight = 18;
    const headerRowHeight = 22;
    const cellPadding = 8;
    const minColWidth = 35; // Largura mínima para evitar colunas muito estreitas
    
    // A largura da tabela é exatamente a largura disponível
    const tableWidth = availableWidth;
    
    // Calcular largura necessária para cada coluna baseada no conteúdo
    let categoryMaxWidth = font.widthOfTextAtSize('Categoria', 8) + cellPadding * 2;
    
    // Verificar largura das categorias (Receita, Despesa, tipos)
    categoryMaxWidth = Math.max(categoryMaxWidth, font.widthOfTextAtSize('Receita', 7) + cellPadding * 2);
    categoryMaxWidth = Math.max(categoryMaxWidth, font.widthOfTextAtSize('Despesa', 7) + cellPadding * 2);
    
    if (monthlyData.revenueTypes && monthlyData.revenueTypes.length > 0) {
      monthlyData.revenueTypes.forEach(revenueType => {
        const typeName = revenueType.typenome || revenueType.typeName || '-';
        let displayName = sanitizeTextForPDF(typeName.charAt(0).toUpperCase() + typeName.slice(1).toLowerCase());
        // Limitar a 10 caracteres no total (incluindo o "-" e "...")
        // Se o nome completo (com "-") tem mais de 10 caracteres, truncar o nome para 6 caracteres e adicionar "..."
        if (displayName.length > 9) {
          displayName = displayName.substring(0, 6) + '...';
        }
        const text = `-${displayName}`;
        categoryMaxWidth = Math.max(categoryMaxWidth, font.widthOfTextAtSize(text, 7) + cellPadding * 2 + 5); // +5 para indentação
      });
    }
    
    if (monthlyData.expenseTypes && monthlyData.expenseTypes.length > 0) {
      monthlyData.expenseTypes.forEach(expenseType => {
        const typeName = expenseType.typenome || expenseType.typeName || '-';
        let displayName = sanitizeTextForPDF(typeName.charAt(0).toUpperCase() + typeName.slice(1).toLowerCase());
        // Limitar a 10 caracteres no total (incluindo o "-" e "...")
        // Se o nome completo (com "-") tem mais de 10 caracteres, truncar o nome para 6 caracteres e adicionar "..."
        if (displayName.length > 9) {
          displayName = displayName.substring(0, 6) + '...';
        }
        const text = `-${displayName}`;
        categoryMaxWidth = Math.max(categoryMaxWidth, font.widthOfTextAtSize(text, 7) + cellPadding * 2 + 5); // +5 para indentação
      });
    }
    
    // Calcular largura necessária para cada mês
    const monthWidths = [];
    months.forEach((month, idx) => {
      let monthMaxWidth = font.widthOfTextAtSize(month, 8) + cellPadding * 2;
      
      // Verificar valores de receita
      if (monthlyData.summary[idx] && monthlyData.summary[idx].receita > 0) {
        const valueText = monthlyData.summary[idx].receita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        monthMaxWidth = Math.max(monthMaxWidth, font.widthOfTextAtSize(valueText, 6) + cellPadding * 2);
      }
      
      // Verificar valores de despesa
      if (monthlyData.summary[idx] && monthlyData.summary[idx].despesa > 0) {
        const valueText = monthlyData.summary[idx].despesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        monthMaxWidth = Math.max(monthMaxWidth, font.widthOfTextAtSize(valueText, 6) + cellPadding * 2);
      }
      
      // Verificar valores dos tipos de receita
      if (monthlyData.revenueTypes && monthlyData.revenueTypes.length > 0) {
        monthlyData.revenueTypes.forEach(revenueType => {
          if (revenueType.monthData[idx] && revenueType.monthData[idx].receita > 0) {
            const valueText = revenueType.monthData[idx].receita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            monthMaxWidth = Math.max(monthMaxWidth, font.widthOfTextAtSize(valueText, 6) + cellPadding * 2);
          }
        });
      }
      
      // Verificar valores dos tipos de despesa
      if (monthlyData.expenseTypes && monthlyData.expenseTypes.length > 0) {
        monthlyData.expenseTypes.forEach(expenseType => {
          if (expenseType.monthData[idx] && expenseType.monthData[idx].despesa > 0) {
            const valueText = expenseType.monthData[idx].despesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            monthMaxWidth = Math.max(monthMaxWidth, font.widthOfTextAtSize(valueText, 6) + cellPadding * 2);
          }
        });
      }
      
      monthWidths.push(Math.max(monthMaxWidth, minColWidth));
    });
    
    // Calcular totais e textos para usar em toda a função
    const totalReceita = monthlyData.summary.reduce((sum, item) => sum + (item && item.receita ? item.receita : 0), 0);
    const totalDespesa = monthlyData.summary.reduce((sum, item) => sum + (item && item.despesa ? item.despesa : 0), 0);
    const totalReceitaText = totalReceita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const totalDespesaText = totalDespesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    // Calcular largura necessária para coluna Total
    let totalColMaxWidth = font.widthOfTextAtSize('Total', 8) + cellPadding * 2;
    totalColMaxWidth = Math.max(totalColMaxWidth, font.widthOfTextAtSize(totalReceitaText, 7) + cellPadding * 2);
    totalColMaxWidth = Math.max(totalColMaxWidth, font.widthOfTextAtSize(totalDespesaText, 7) + cellPadding * 2);
    
    // Verificar totais dos tipos
    if (monthlyData.revenueTypes && monthlyData.revenueTypes.length > 0) {
      monthlyData.revenueTypes.forEach(revenueType => {
        const totalRevenue = revenueType.monthData.reduce((sum, item) => sum + (item?.receita || 0), 0);
        if (totalRevenue > 0) {
          const valueText = totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          totalColMaxWidth = Math.max(totalColMaxWidth, font.widthOfTextAtSize(valueText, 7) + cellPadding * 2);
        }
      });
    }
    
    if (monthlyData.expenseTypes && monthlyData.expenseTypes.length > 0) {
      monthlyData.expenseTypes.forEach(expenseType => {
        const totalExpense = expenseType.monthData.reduce((sum, item) => sum + (item?.despesa || 0), 0);
        if (totalExpense > 0) {
          const valueText = totalExpense.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          totalColMaxWidth = Math.max(totalColMaxWidth, font.widthOfTextAtSize(valueText, 7) + cellPadding * 2);
        }
      });
    }
    
    const totalColWidth = Math.max(totalColMaxWidth, minColWidth);
    
    // Calcular largura total necessária
    const totalNeededWidth = categoryMaxWidth + monthWidths.reduce((sum, w) => sum + w, 0) + totalColWidth;
    
    // Ajustar proporcionalmente se necessário (baseado na largura da tabela)
    let categoryColWidth = categoryMaxWidth;
    let finalMonthWidths = [...monthWidths];
    
    if (totalNeededWidth > tableWidth) {
      // Reduzir proporcionalmente baseado na largura da tabela
      const availableWidth = tableWidth - totalColWidth;
      const neededWidthWithoutTotal = categoryMaxWidth + monthWidths.reduce((sum, w) => sum + w, 0);
      if (neededWidthWithoutTotal > 0) {
        const scale = availableWidth / neededWidthWithoutTotal;
        categoryColWidth = Math.max(categoryMaxWidth * scale, minColWidth);
        finalMonthWidths = monthWidths.map(w => Math.max(w * scale, minColWidth));
      }
    } else {
      // Distribuir espaço extra proporcionalmente
      const extraSpace = tableWidth - totalNeededWidth;
      const columnsCount = 1 + monthWidths.length; // categoria + meses
      if (columnsCount > 0) {
        const extraPerColumn = extraSpace / columnsCount;
        categoryColWidth += extraPerColumn;
        finalMonthWidths = monthWidths.map(w => w + extraPerColumn);
      }
    }
    
    // Garantir que a soma das larguras finais seja exatamente igual a tableWidth
    const currentTotalWidth = categoryColWidth + finalMonthWidths.reduce((sum, w) => sum + w, 0) + totalColWidth;
    if (Math.abs(currentTotalWidth - tableWidth) > 0.1) {
      // Ajustar diferença (pode haver pequenos erros de arredondamento)
      const diff = tableWidth - currentTotalWidth;
      // Distribuir a diferença proporcionalmente entre as colunas
      const columnsCount = 1 + finalMonthWidths.length; // categoria + meses
      if (columnsCount > 0 && Math.abs(diff) > 0.01) {
        const adjustmentPerCol = diff / columnsCount;
        categoryColWidth += adjustmentPerCol;
        finalMonthWidths = finalMonthWidths.map(w => w + adjustmentPerCol);
      }
    }
    
    // Recalcular para garantir precisão
    let finalTotalWidth = categoryColWidth + finalMonthWidths.reduce((sum, w) => sum + w, 0) + totalColWidth;
    if (Math.abs(finalTotalWidth - tableWidth) > 0.01) {
      // Ajustar na última coluna de mês se ainda houver diferença
      const finalDiff = tableWidth - finalTotalWidth;
      if (finalMonthWidths.length > 0) {
        finalMonthWidths[finalMonthWidths.length - 1] += finalDiff;
      }
      // Recalcular após ajuste
      finalTotalWidth = categoryColWidth + finalMonthWidths.reduce((sum, w) => sum + w, 0) + totalColWidth;
    }
    
    // Garantir que a largura final não ultrapasse a largura disponível
    // Usar a menor entre a largura calculada e a largura disponível
    const actualTableWidth = Math.min(finalTotalWidth, tableWidth);
    
    let currentY = startY;
    const tableX = startX + tableMargin;
    
    // Título
    page.drawText('Resumo Financeiro:', {
      x: tableX,
      y: currentY,
      size: 10,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    currentY -= 20;
    
    // Cabeçalho da tabela
    const headerY = currentY;
    page.drawRectangle({
      x: tableX,
      y: currentY - headerRowHeight,
      width: actualTableWidth,
      height: headerRowHeight,
      borderColor: rgb(0.7, 0.7, 0.7),
      borderWidth: 1,
      color: rgb(0.95, 0.95, 0.95),
    });
    
    // Cabeçalho "Categoria"
    page.drawText('Categoria', {
      x: tableX + cellPadding,
      y: currentY - 15,
      size: 8,
      font: boldFont,
      color: rgb(0.4, 0.4, 0.4),
    });
    
    // Cabeçalhos dos meses (usando larguras calculadas dinamicamente)
    let monthX = tableX + categoryColWidth;
    months.forEach((month, index) => {
      const monthColWidth = finalMonthWidths[index];
      page.drawText(month, {
        x: monthX + (monthColWidth - font.widthOfTextAtSize(month, 8)) / 2,
        y: currentY - 15,
        size: 8,
        font: boldFont,
        color: rgb(0.4, 0.4, 0.4),
      });
      monthX += monthColWidth;
    });
    
    // Cabeçalho "Total"
    page.drawText('Total', {
      x: monthX + (totalColWidth - font.widthOfTextAtSize('Total', 8)) / 2,
      y: currentY - 15,
      size: 8,
      font: boldFont,
      color: rgb(0.4, 0.4, 0.4),
    });
    
    currentY -= headerRowHeight + 5;
    
    // Linha de separação vazia
    currentY -= 10;
    
    // Os totais já foram calculados acima para calcular as larguras
    // Linha de Receita
    const receitaY = currentY;
    page.drawRectangle({
      x: tableX,
      y: currentY - rowHeight,
      width: actualTableWidth,
      height: rowHeight,
      borderColor: rgb(0.8, 0.8, 0.8),
      borderWidth: 0.5,
    });
    
    page.drawText('Receita', {
      x: tableX + cellPadding,
      y: currentY - 13,
      size: 7,
      font: boldFont,
      color: rgb(0.133, 0.773, 0.369), // Verde
    });
    
    monthX = tableX + categoryColWidth;
    monthlyData.summary.forEach((monthData, idx) => {
      const monthColWidth = finalMonthWidths[idx];
      const value = monthData && monthData.receita > 0 ? monthData.receita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
      const valueWidth = font.widthOfTextAtSize(value, 6);
      page.drawText(value, {
        x: monthX + Math.max(0, (monthColWidth - valueWidth - cellPadding)),
        y: currentY - 13,
        size: 6,
        font: font,
        color: rgb(0, 0, 0),
      });
      monthX += monthColWidth;
    });
    
    // Verificar se não ultrapassou os limites da tabela antes de desenhar Total
    if (monthX + totalColWidth > tableX + actualTableWidth) {
      // Ajustar a posição da coluna Total para ficar dentro dos limites
      monthX = tableX + actualTableWidth - totalColWidth;
    }
    
    // Total de Receita (texto já foi calculado acima)
    const totalReceitaWidth = font.widthOfTextAtSize(totalReceitaText, 7);
    page.drawText(totalReceitaText, {
      x: monthX + Math.max(0, (totalColWidth - totalReceitaWidth - cellPadding)),
      y: currentY - 13,
      size: 7,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    currentY -= rowHeight;
    
    // Tipos de Receita
    if (monthlyData.revenueTypes && monthlyData.revenueTypes.length > 0) {
      monthlyData.revenueTypes.forEach((revenueType) => {
        const totalRevenue = revenueType.monthData.reduce((sum, item) => sum + (item?.receita || 0), 0);
        
        page.drawRectangle({
          x: tableX,
          y: currentY - rowHeight,
          width: actualTableWidth,
          height: rowHeight,
          borderColor: rgb(0.8, 0.8, 0.8),
          borderWidth: 0.5,
        });
        
        const typeName = revenueType.typenome || revenueType.typeName || '-';
        let displayName = sanitizeTextForPDF(typeName.charAt(0).toUpperCase() + typeName.slice(1).toLowerCase());
        // Limitar a 10 caracteres no total (incluindo o "-" e "...")
        // Se o nome completo (com "-") tem mais de 10 caracteres, truncar o nome para 6 caracteres e adicionar "..."
        if (displayName.length > 9) {
          displayName = displayName.substring(0, 6) + '...';
        }
        page.drawText(`-${displayName}`, {
          x: tableX + cellPadding + 5, // Indentação
          y: currentY - 13,
          size: 7,
          font: font,
          color: rgb(0.2, 0.5, 0.9), // Azul
        });
        
        monthX = tableX + categoryColWidth;
        revenueType.monthData.forEach((monthData, idx) => {
          const monthColWidth = finalMonthWidths[idx];
          const value = monthData && monthData.receita > 0 ? monthData.receita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
          const valueWidth = font.widthOfTextAtSize(value, 6);
          page.drawText(value, {
            x: monthX + Math.max(0, (monthColWidth - valueWidth - cellPadding)),
            y: currentY - 13,
            size: 6,
            font: font,
            color: rgb(0, 0, 0),
          });
          monthX += monthColWidth;
        });
        
        // Verificar limites antes de desenhar Total
        if (monthX + totalColWidth > tableX + actualTableWidth) {
          monthX = tableX + actualTableWidth - totalColWidth;
        }
        
        // Total do tipo de receita
        const totalRevenueText = totalRevenue > 0 ? totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
        const totalRevenueWidth = font.widthOfTextAtSize(totalRevenueText, 7);
        page.drawText(totalRevenueText, {
          x: monthX + Math.max(0, (totalColWidth - totalRevenueWidth - cellPadding)),
          y: currentY - 13,
          size: 7,
          font: font,
          color: rgb(0, 0, 0),
        });
        
        currentY -= rowHeight;
      });
    }
    
    // Linha de separação vazia
    currentY -= 10;
    
    // Linha de Despesa
    page.drawRectangle({
      x: tableX,
      y: currentY - rowHeight,
      width: actualTableWidth,
      height: rowHeight,
      borderColor: rgb(0.8, 0.8, 0.8),
      borderWidth: 0.5,
    });
    
    page.drawText('Despesa', {
      x: tableX + cellPadding,
      y: currentY - 13,
      size: 7,
      font: boldFont,
      color: rgb(0.937, 0.267, 0.267), // Vermelho
    });
    
    monthX = tableX + categoryColWidth;
    monthlyData.summary.forEach((monthData, idx) => {
      const monthColWidth = finalMonthWidths[idx];
      const value = monthData && monthData.despesa > 0 ? monthData.despesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
      const valueWidth = font.widthOfTextAtSize(value, 6);
      page.drawText(value, {
        x: monthX + Math.max(0, (monthColWidth - valueWidth - cellPadding)),
        y: currentY - 13,
        size: 6,
        font: font,
        color: rgb(0, 0, 0),
      });
      monthX += monthColWidth;
    });
    
    // Verificar limites antes de desenhar Total
    if (monthX + totalColWidth > tableX + actualTableWidth) {
      monthX = tableX + actualTableWidth - totalColWidth;
    }
    
    // Total de Despesa (texto já foi calculado acima)
    const totalDespesaWidth = font.widthOfTextAtSize(totalDespesaText, 7);
    page.drawText(totalDespesaText, {
      x: monthX + Math.max(0, (totalColWidth - totalDespesaWidth - cellPadding)),
      y: currentY - 13,
      size: 7,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    currentY -= rowHeight;
    
    // Tipos de Despesa
    if (monthlyData.expenseTypes && monthlyData.expenseTypes.length > 0) {
      monthlyData.expenseTypes.forEach((expenseType) => {
        const totalExpense = expenseType.monthData.reduce((sum, item) => sum + (item?.despesa || 0), 0);
        
        page.drawRectangle({
          x: tableX,
          y: currentY - rowHeight,
          width: actualTableWidth,
          height: rowHeight,
          borderColor: rgb(0.8, 0.8, 0.8),
          borderWidth: 0.5,
        });
        
        const typeName = expenseType.typenome || expenseType.typeName || '-';
        let displayName = sanitizeTextForPDF(typeName.charAt(0).toUpperCase() + typeName.slice(1).toLowerCase());
        // Limitar a 10 caracteres no total (incluindo o "-" e "...")
        // Se o nome completo (com "-") tem mais de 10 caracteres, truncar o nome para 6 caracteres e adicionar "..."
        if (displayName.length > 9) {
          displayName = displayName.substring(0, 6) + '...';
        }
        page.drawText(`-${displayName}`, {
          x: tableX + cellPadding + 5, // Indentação
          y: currentY - 13,
          size: 7,
          font: font,
          color: rgb(0.9, 0.5, 0.1), // Laranja
        });
        
        monthX = tableX + categoryColWidth;
        expenseType.monthData.forEach((monthData, idx) => {
          const monthColWidth = finalMonthWidths[idx];
          const value = monthData && monthData.despesa > 0 ? monthData.despesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
          const valueWidth = font.widthOfTextAtSize(value, 6);
          page.drawText(value, {
            x: monthX + Math.max(0, (monthColWidth - valueWidth - cellPadding)),
            y: currentY - 13,
            size: 6,
            font: font,
            color: rgb(0, 0, 0),
          });
          monthX += monthColWidth;
        });
        
        // Verificar limites antes de desenhar Total
        if (monthX + totalColWidth > tableX + actualTableWidth) {
          monthX = tableX + actualTableWidth - totalColWidth;
        }
        
        // Total do tipo de despesa
        const totalExpenseText = totalExpense > 0 ? totalExpense.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
        const totalExpenseWidth = font.widthOfTextAtSize(totalExpenseText, 7);
        page.drawText(totalExpenseText, {
          x: monthX + Math.max(0, (totalColWidth - totalExpenseWidth - cellPadding)),
          y: currentY - 13,
          size: 7,
          font: font,
          color: rgb(0, 0, 0),
        });
        
        currentY -= rowHeight;
      });
    }
    
    // Retornar a altura total usada pela tabela
    const heightUsed = startY - currentY;
    return heightUsed;
  };

  // Função auxiliar para desenhar gráfico no PDF
  const drawChartInPDF = (page, chartData, startX, startY, width, height, font, boldFont) => {
    if (!chartData || chartData.length === 0) return;

    // Cores
    const receitaColor = rgb(0.133, 0.773, 0.369); // #22c55e (verde)
    const despesaColor = rgb(0.937, 0.267, 0.267); // #ef4444 (vermelho)
    const gridColor = rgb(0.95, 0.95, 0.95);
    const axisColor = rgb(0.4, 0.4, 0.4);

    // Área do gráfico
    const chartPadding = 40;
    const chartPaddingTop = 25; // Padding no topo (espaço do título)
    const chartPaddingBottom = 30; // Espaço para rótulos do eixo X (abaixo do gráfico) e legenda
    const chartX = startX + chartPadding;
    const chartY = startY - height + chartPaddingTop;
    const chartWidth = width - (chartPadding * 2);
    // Usar toda a altura disponível menos os paddings necessários
    // A legenda fica abaixo do gráfico, então precisa estar dentro do padding bottom
    const chartHeight = height - chartPaddingTop - chartPaddingBottom;

    // Encontrar valores máximos para escalar o gráfico (adaptativo, como no Recharts)
    let maxReceita = 0;
    let maxDespesa = 0;
    chartData.forEach(item => {
      maxReceita = Math.max(maxReceita, item.receita || 0);
      maxDespesa = Math.max(maxDespesa, item.despesas || 0);
    });
    // Usar o valor máximo real dos dados (adaptativo)
    // Se todos os valores forem 0, usar um valor mínimo pequeno (ex: 10) para evitar divisão por zero e manter escala visível
    const rawMaxValue = Math.max(maxReceita, maxDespesa);
    const maxValue = rawMaxValue === 0 ? 10 : rawMaxValue;
    const scale = maxValue > 0 ? chartHeight / maxValue : 0;

    // Desenhar grade de fundo
    const gridLines = 5;
    for (let i = 0; i <= gridLines; i++) {
      // Calcular posição Y da linha de grade (de baixo para cima)
      const y = chartY + (i * chartHeight / gridLines);
      // Valor correspondente: menor no topo da iteração (i=0 = base = 0), maior no final (i=gridLines = topo = maxValue)
      const value = (i * maxValue / gridLines);
      
      // Linha de grade
      page.drawLine({
        start: { x: chartX, y: y },
        end: { x: chartX + chartWidth, y: y },
        thickness: 0.5,
        color: gridColor,
      });

      // Rótulo do eixo Y (valores de baixo para cima: 0 na base, maxValue no topo)
      const label = `R$ ${Math.round(value).toLocaleString('pt-BR')}`;
      const labelWidth = font.widthOfTextAtSize(label, 8);
      page.drawText(label, {
        x: chartX - labelWidth - 5,
        y: y - 4,
        size: 8,
        font: font,
        color: axisColor,
      });
    }

    // Desenhar eixos
    // Eixo Y (vertical)
    page.drawLine({
      start: { x: chartX, y: chartY },
      end: { x: chartX, y: chartY + chartHeight },
      thickness: 1,
      color: axisColor,
    });

    // Eixo X (horizontal)
    page.drawLine({
      start: { x: chartX, y: chartY },
      end: { x: chartX + chartWidth, y: chartY },
      thickness: 1,
      color: axisColor,
    });

    // Desenhar dados
    const numPoints = chartData.length;
    const pointSpacing = numPoints > 1 ? chartWidth / (numPoints - 1) : 0;
    const receitaPoints = [];
    const despesaPoints = [];

    chartData.forEach((item, index) => {
      const x = numPoints > 1 
        ? chartX + (index * pointSpacing)
        : chartX + (chartWidth / 2); // Centralizar se houver apenas um ponto
      // No PDF, Y cresce de baixo para cima (como no Recharts)
      // chartY é a base (Y menor), então valor 0 fica em chartY e valor maxValue fica em chartY + chartHeight
      const receitaY = chartY + ((item.receita || 0) * scale);
      const despesaY = chartY + ((item.despesas || 0) * scale);

      receitaPoints.push({ x, y: receitaY });
      despesaPoints.push({ x, y: despesaY });

      // Rótulo do eixo X (usando item.nome, que corresponde ao dataKey do Recharts)
      const label = item.nome || '';
      const labelWidth = font.widthOfTextAtSize(label, 7);
      page.drawText(label, {
        x: x - (labelWidth / 2),
        y: chartY - 20,
        size: 7,
        font: font,
        color: axisColor,
      });
    });

    // Desenhar linhas (apenas se houver mais de um ponto)
    if (receitaPoints.length > 1) {
      // Linha de receitas (verde)
      for (let i = 0; i < receitaPoints.length - 1; i++) {
        page.drawLine({
          start: receitaPoints[i],
          end: receitaPoints[i + 1],
          thickness: 2,
          color: receitaColor,
        });
      }
    }

    if (despesaPoints.length > 1) {
      // Linha de despesas (vermelho)
      for (let i = 0; i < despesaPoints.length - 1; i++) {
        page.drawLine({
          start: despesaPoints[i],
          end: despesaPoints[i + 1],
          thickness: 2,
          color: despesaColor,
        });
      }
    }

    // Desenhar pontos (pequenos retângulos)
    const pointSize = 3;
    receitaPoints.forEach(point => {
      page.drawRectangle({
        x: point.x - pointSize / 2,
        y: point.y - pointSize / 2,
        width: pointSize,
        height: pointSize,
        color: receitaColor,
      });
    });

    despesaPoints.forEach(point => {
      page.drawRectangle({
        x: point.x - pointSize / 2,
        y: point.y - pointSize / 2,
        width: pointSize,
        height: pointSize,
        color: despesaColor,
      });
    });

    // Legenda (em linha, centralizada) - posicionada abaixo do gráfico e dos rótulos do eixo X
    // O gráfico termina em chartY (base), os rótulos ficam em chartY - 20, então a legenda fica abaixo
    const legendY = chartY - 40; // Espaço entre os rótulos do eixo X e a legenda
    const legendItemSpacing = 40; // Espaçamento entre os itens da legenda
    const legendBoxSize = 10;
    const legendBoxHeight = 3;
    const legendTextSpacing = 15; // Espaçamento entre o box e o texto
    
    // Calcular largura total da legenda para centralizar
    const receitasTextWidth = font.widthOfTextAtSize('Receitas', 9);
    const despesasTextWidth = font.widthOfTextAtSize('Despesas', 9);
    const receitasItemWidth = legendBoxSize + legendTextSpacing + receitasTextWidth;
    const despesasItemWidth = legendBoxSize + legendTextSpacing + despesasTextWidth;
    const totalLegendWidth = receitasItemWidth + legendItemSpacing + despesasItemWidth;
    
    // Posição X inicial para centralizar (centro da área do gráfico menos metade da largura total)
    const legendStartX = (chartX + chartWidth / 2) - (totalLegendWidth / 2);
    let currentLegendX = legendStartX;

    // Receitas
    page.drawRectangle({
      x: currentLegendX,
      y: legendY,
      width: legendBoxSize,
      height: legendBoxHeight,
      color: receitaColor,
    });
    page.drawText('Receitas', {
      x: currentLegendX + legendBoxSize + legendTextSpacing,
      y: legendY - 2,
      size: 9,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    currentLegendX += receitasItemWidth + legendItemSpacing;

    // Despesas
    page.drawRectangle({
      x: currentLegendX,
      y: legendY,
      width: legendBoxSize,
      height: legendBoxHeight,
      color: despesaColor,
    });
    page.drawText('Despesas', {
      x: currentLegendX + legendBoxSize + legendTextSpacing,
      y: legendY - 2,
      size: 9,
      font: font,
      color: rgb(0, 0, 0),
    });

    // Título do gráfico (alinhado à esquerda)
    page.drawText('Receitas vs Despesas:', {
      x: startX,
      y: startY - 10,
      size: 10,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
  };


  
  // Função para gerar PDF das transações
  const handleDownloadPDF = async () => {
    try {
      const filteredTransactions = getFilteredTransactions();
      
      if (filteredTransactions.length === 0) {
        await showConfirmation({
          title: 'Nenhuma transação',
          message: 'Não há transações no período filtrado para gerar o PDF.',
          confirmText: 'OK',
          showCancel: false,
          type: 'info'
        });
        return;
      }

      const pdfDoc = await PDFDocument.create();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const margin = 50;
      const topMargin = 50;
      const rowHeight = 20;
      const headerHeight = 25;

      // Criar primeira página
      let currentPage = pdfDoc.addPage([595, 842]); // A4 size
      let yPosition = currentPage.getHeight() - topMargin;
      const pageWidth = currentPage.getWidth() - (margin * 2);

      // Título
      const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const periodText = monthFilter 
        ? `${monthNames[parseInt(monthFilter) - 1]} de ${yearFilter}`
        : `Ano ${yearFilter}`;
      const titleText = `Transações - ${periodText}`;
      const titleWidth = helveticaBoldFont.widthOfTextAtSize(titleText, 14);
      currentPage.drawText(titleText, {
        x: (currentPage.getWidth() - titleWidth) / 2,
        y: yPosition,
        size: 14,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0),
      });
      yPosition -= 35;

      // Calcular totais para os cards
      const totalRevenue = filteredTransactions
        .filter(t => t.tipo === 'receita')
        .reduce((sum, t) => sum + parseFloat(t.valor || t.amount || 0), 0);
      const totalExpenses = filteredTransactions
        .filter(t => t.tipo === 'despesa')
        .reduce((sum, t) => sum + parseFloat(t.valor || t.amount || 0), 0);
      const lucroLiquido = totalRevenue - totalExpenses;

      // Desenhar os 3 cards de valores
      const cardHeight = 50;
      const cardSpacing = 10;
      const cardWidth = (pageWidth - cardSpacing * 2) / 3;
      
      // Card 1: Faturamento
      currentPage.drawRectangle({
        x: margin,
        y: yPosition - cardHeight,
        width: cardWidth,
        height: cardHeight,
        borderColor: rgb(0.8, 0.8, 0.8),
        borderWidth: 1,
      });
      currentPage.drawText('Faturamento', {
        x: margin + 10,
        y: yPosition - 15,
        size: 9,
        font: helveticaFont,
        color: rgb(0.5, 0.5, 0.5),
      });
      const faturamentoText = `R$ ${totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      currentPage.drawText(faturamentoText, {
        x: margin + 10,
        y: yPosition - 35,
        size: 12,
        font: helveticaBoldFont,
        color: rgb(0.133, 0.773, 0.369), // Verde
      });

      // Card 2: Custos
      const card2X = margin + cardWidth + cardSpacing;
      currentPage.drawRectangle({
        x: card2X,
        y: yPosition - cardHeight,
        width: cardWidth,
        height: cardHeight,
        borderColor: rgb(0.8, 0.8, 0.8),
        borderWidth: 1,
      });
      currentPage.drawText('Custos', {
        x: card2X + 10,
        y: yPosition - 15,
        size: 9,
        font: helveticaFont,
        color: rgb(0.5, 0.5, 0.5),
      });
      const custosText = `R$ ${totalExpenses.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      currentPage.drawText(custosText, {
        x: card2X + 10,
        y: yPosition - 35,
        size: 12,
        font: helveticaBoldFont,
        color: rgb(0.937, 0.267, 0.267), // Vermelho
      });

      // Card 3: Lucro Líquido
      const card3X = margin + (cardWidth + cardSpacing) * 2;
      currentPage.drawRectangle({
        x: card3X,
        y: yPosition - cardHeight,
        width: cardWidth,
        height: cardHeight,
        borderColor: rgb(0.8, 0.8, 0.8),
        borderWidth: 1,
      });
      currentPage.drawText('Lucro Líquido', {
        x: card3X + 10,
        y: yPosition - 15,
        size: 9,
        font: helveticaFont,
        color: rgb(0.5, 0.5, 0.5),
      });
      currentPage.drawText('faturamento - custos', {
        x: card3X + 10,
        y: yPosition - 25,
        size: 7,
        font: helveticaFont,
        color: rgb(0.6, 0.6, 0.6),
      });
      const lucroText = `R$ ${lucroLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const lucroColor = lucroLiquido >= 0 ? rgb(0.133, 0.773, 0.369) : rgb(0.937, 0.267, 0.267);
      currentPage.drawText(lucroText, {
        x: card3X + 10,
        y: yPosition - 42,
        size: 12,
        font: helveticaBoldFont,
        color: lucroColor,
      });

      yPosition -= cardHeight + 20;

      // Linha separadora abaixo dos cards
      currentPage.drawLine({
        start: { x: margin, y: yPosition },
        end: { x: currentPage.getWidth() - margin, y: yPosition },
        thickness: 0.5,
        color: rgb(0.6, 0.6, 0.6),
      });
      yPosition -= 30;

      // Título da seção de transações
      currentPage.drawText('Transações Detalhadas:', {
        x: margin,
        y: yPosition,
        size: 10,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0),
      });
      yPosition -= 25;

      // Cabeçalho da tabela
      const headers = ['Data', 'Tipo', 'Pet', 'Descrição', 'Valor (R$)'];
      const columnWidths = [70, 120, 100, 150, 100];
      let xPosition = margin;

      headers.forEach((header, index) => {
        currentPage.drawText(header, {
          x: xPosition,
          y: yPosition,
          size: 10,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
        xPosition += columnWidths[index];
      });

      yPosition -= headerHeight;

      // Dados das transações
      filteredTransactions.forEach((transaction) => {
        // Verificar se precisa de nova página
        if (yPosition < 50) {
          currentPage = pdfDoc.addPage([595, 842]);
          currentPage.drawText('(Continuação)', {
            x: margin,
            y: currentPage.getHeight() - 30,
            size: 10,
            font: helveticaFont,
            color: rgb(0.5, 0.5, 0.5),
          });
          yPosition = currentPage.getHeight() - topMargin;
        }

        const date = getTransactionDate(transaction);
        const dateStr = date ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : '-';
        const type = transaction.tipo === 'receita' ? 'Receita' : 'Despesa';
        
        // Obter nome do tipo de transação
        let tipoTransacao = '-';
        if (transaction.nomeTipoTransacao) {
          tipoTransacao = transaction.nomeTipoTransacao.toString().replace(/[\r\n\t]/g, ' ').trim();
        } else if (transaction.idTipoTransacao && transactionTypes && transactionTypes.length > 0) {
          const typeObj = transactionTypes.find(t => t.id === transaction.idTipoTransacao);
          tipoTransacao = typeObj?.nome || '-';
        }
        // Formatar para ter apenas a primeira letra maiúscula
        if (tipoTransacao && tipoTransacao !== '-') {
          tipoTransacao = tipoTransacao.charAt(0).toUpperCase() + tipoTransacao.slice(1).toLowerCase();
        }
        
        // Obter descrição da transação (campo descricao ou description)
        let descricao = transaction.descricao || transaction.description || '-';
        descricao = descricao.toString().replace(/[\r\n\t]/g, ' ').trim();
        
        // Obter nome do pet
        let nomePet = '-';
        if (transaction.nomePet) {
          nomePet = transaction.nomePet.toString().replace(/[\r\n\t]/g, ' ').trim();
        }
        
        const amount = parseFloat(transaction.valor || transaction.amount || 0);
        const formattedAmount = amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        // Adicionar símbolo + para receita e - para despesa
        const amountStr = transaction.tipo === 'receita' ? `+${formattedAmount}` : `-${formattedAmount}`;

        xPosition = margin;
        // Ordem: Data, Tipo, Pet, Descrição, Valor
        const rowData = [dateStr, tipoTransacao, nomePet, descricao, amountStr];

        rowData.forEach((text, index) => {
          // Limpar texto: remover quebras de linha e caracteres não suportados
          let cleanText = text.toString().replace(/[\r\n\t]/g, ' ').replace(/[^\x20-\x7E\u00A0-\u00FF]/g, '');
          
          // Truncar texto se necessário
          const maxWidth = columnWidths[index] - 5;
          let displayText = cleanText;
          while (helveticaFont.widthOfTextAtSize(displayText, 9) > maxWidth && displayText.length > 0) {
            displayText = displayText.slice(0, -1);
          }
          if (cleanText.length > displayText.length) {
            displayText = displayText.slice(0, -3) + '...';
          }

          currentPage.drawText(displayText, {
            x: xPosition,
            y: yPosition,
            size: 9,
            font: helveticaFont,
            color: type === 'Receita' ? rgb(0, 0.5, 0) : rgb(0.8, 0, 0),
          });
          xPosition += columnWidths[index];
        });

        yPosition -= rowHeight;
      });

      // Linha separadora e mensagem final
      yPosition -= 15;
      
      // Verificar se precisa de nova página para o resumo financeiro
      if (yPosition < 50) {
        currentPage = pdfDoc.addPage([595, 842]);
        yPosition = currentPage.getHeight() - topMargin;
      }

      yPosition -= 10;
      
      // Mensagem informando fim da listagem
      const endMessage = 'Fim da listagem de transações';
      currentPage.drawText(endMessage, {
        x: margin,
        y: yPosition,
        size: 9,
        font: helveticaFont,
        color: rgb(0.5, 0.5, 0.5),
      });
      yPosition -= 15;

      // Desenhar Resumo Financeiro (tabela mensal detalhada) - APÓS a lista de transações
      const estimatedTableHeight = 300; // Altura estimada da tabela
      
      // Verificar se precisa de nova página para o resumo financeiro
      if (yPosition - estimatedTableHeight < 50) {
        currentPage = pdfDoc.addPage([595, 842]);
        yPosition = currentPage.getHeight() - topMargin;
      }
      
      // Linha separadora antes do resumo financeiro
      currentPage.drawLine({
        start: { x: margin, y: yPosition },
        end: { x: currentPage.getWidth() - margin, y: yPosition },
        thickness: 0.5,
        color: rgb(0.6, 0.6, 0.6),
      });
      yPosition -= 30;
      
      const tableHeight = drawMonthlyFinancialTableInPDF(currentPage, monthlyData, margin, yPosition, pageWidth, helveticaFont, helveticaBoldFont);
      yPosition -= tableHeight + 20;

      // Desenhar gráfico de receitas vs despesas no final
      if (chartData && chartData.length > 0) {
        // Altura total para o gráfico (incluindo título e legenda)
        const totalChartHeight = 250;
        const chartWidth = pageWidth;
        
        // Espaçamento antes da linha separadora
        yPosition -= 15;
        
        // Verificar se precisa de nova página para o gráfico
        if (yPosition - totalChartHeight < 50) {
          currentPage = pdfDoc.addPage([595, 842]);
          yPosition = currentPage.getHeight() - topMargin;
          // Redesenhar a linha separadora na nova página
          yPosition -= 15;
        }

        // Linha separadora antes do gráfico (entre Resumo Financeiro e gráfico)
        currentPage.drawLine({
          start: { x: margin, y: yPosition },
          end: { x: currentPage.getWidth() - margin, y: yPosition },
          thickness: 0.5,
          color: rgb(0.6, 0.6, 0.6),
        });
        yPosition -= 20;

        // Passar altura total, a função calculará a área útil do gráfico internamente
        drawChartInPDF(currentPage, chartData, margin, yPosition, chartWidth, totalChartHeight, helveticaFont, helveticaBoldFont);
        yPosition -= totalChartHeight + 20;
      }

      // Salvar PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `transacoes_${yearFilter}${monthFilter ? `_${String(monthFilter).padStart(2, '0')}` : ''}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      await showConfirmation({
        title: 'Erro ao gerar PDF',
          message: `Erro ao gerar o PDF: ${error.message || 'Erro desconhecido'}`,
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  if (loading || transactionsLoading || appointmentsLoading || typesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground-light dark:text-foreground-dark flex items-center space-x-2">
          <BarChart3 className="w-6 h-6" />
          <span>Dashboard</span>
        </h1>
        <p className="text-muted-light dark:text-muted-dark">Visão geral do seu negócio de claw machines</p>
      </div>

      {/* Resumo Financeiro */}
      <div className="card py-2">
        <div className="grid grid-cols-3 gap-0">
          <div className="flex flex-col items-center justify-center text-center border-r border-border-light dark:border-border-dark">
            <p className="text-base font-medium text-muted-light dark:text-muted-dark mb-1">Faturamento</p>
            <p className="text-2xl font-bold text-success-600">
              R$ {stats.totalRevenue.toLocaleString('pt-BR')}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center text-center border-r border-border-light dark:border-border-dark">
            <p className="text-base font-medium text-muted-light dark:text-muted-dark mb-1">Custos</p>
            <p className="text-2xl font-bold text-danger-600">
              R$ {stats.totalExpenses.toLocaleString('pt-BR')}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-base font-medium text-muted-light dark:text-muted-dark mb-0">Lucro Líquido</p>
            <p className="text-xs text-muted-light dark:text-muted-dark mb-0" style={{ fontSize: '0.65rem' }}>faturamento - custos</p>
            <p className={`text-2xl font-bold ${financialSummary.lucroLiquido >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
              R$ {financialSummary.lucroLiquido.toLocaleString('pt-BR')}
            </p>
          </div>

          
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {availableYears.length > 0 && (
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-primary-600" />
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="input-field max-w-xs"
              >
                {availableYears.map(year => (
                  <option key={year} value={year.toString()}>{year}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-primary-600" />
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="input-field max-w-xs"
            >
              <option value="">Todos os meses</option>
              <option value="1">Janeiro</option>
              <option value="2">Fevereiro</option>
              <option value="3">Março</option>
              <option value="4">Abril</option>
              <option value="5">Maio</option>
              <option value="6">Junho</option>
              <option value="7">Julho</option>
              <option value="8">Agosto</option>
              <option value="9">Setembro</option>
              <option value="10">Outubro</option>
              <option value="11">Novembro</option>
              <option value="12">Dezembro</option>
            </select>
          </div>
        </div>
        <button
          onClick={handleDownloadPDF}
          className="btn-primary flex items-center space-x-2"
        >
          <Download className="w-5 h-5" />
          <span>Baixar PDF</span>
        </button>
      </div>

      {/* Tabela Mensal */}
      <MonthlyFinancialTable data={monthlyData} />

      <AppointmentsMonthSummary
        monthlyAggregates={appointmentsMonthlyAggregates}
        availableYears={appointmentYears}
      />

      {/* Gráficos */}
      <div className="space-y-6">
        <RevenueChart data={chartData} monthFilter={monthFilter} yearFilter={yearFilter} />
      </div>

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        showCancel={confirmation.showCancel}
        type={confirmation.type}
      />
    </div>
  );
};

export default Dashboard;
